import React from "react";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common/UI";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Stethoscope, Bed, Receipt, Plus } from "@phosphor-icons/react";

export default function DashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();

  const isSuperuser = user?.role === "superuser";
  const opdHidden = user?.hospital?.opd_module_enabled === false;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Dashboard"
        title={`Hello, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle={
          isSuperuser
            ? "You have superuser access. Provision hospitals, manage admins, and oversee all operations."
            : "Quick access to OPD, IPD and billing flows."
        }
        actions={
          isSuperuser ? (
            <Button onClick={() => nav("/hospitals")} data-testid="manage-hospitals-btn"
              className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
              Manage hospitals <ArrowRight size={14} weight="bold" className="ml-2" />
            </Button>
          ) : (
            <>
              <Button onClick={() => nav("/patients?register=1")} variant="outline" data-testid="dash-go-patients"
                className="rounded-sm border-[var(--hms-border-2)]">
                <Plus size={14} weight="bold" className="mr-1.5" /> Register patient
              </Button>
              <Button onClick={() => nav("/ipd")} data-testid="dash-go-ipd"
                className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
                IPD board <ArrowRight size={14} weight="bold" className="ml-2" />
              </Button>
            </>
          )
        }
      />

      {!isSuperuser && (
        <div className={`grid grid-cols-1 gap-4 ${opdHidden ? "lg:grid-cols-2" : "lg:grid-cols-3"}`}>
          {!opdHidden && (
            <QuickAction
              title="Record OPD visit" desc="Quick consult: complaints, Rx, fee."
              icon={Stethoscope} onClick={() => nav("/opd")} testid="qa-opd"
            />
          )}
          <QuickAction
            title="Admit patient (IPD)" desc="Assign ward & bed, generate admit card."
            icon={Bed} onClick={() => nav("/ipd")} testid="qa-ipd"
          />
          <QuickAction
            title="Generate bill" desc="Itemized charges + payment receipt PDF."
            icon={Receipt} onClick={() => nav("/ipd")} testid="qa-billing"
          />
        </div>
      )}

      {isSuperuser && (
        <div className="hms-card p-6">
          <div className="hms-section-title mb-2">Tip</div>
          <p className="text-sm text-[var(--hms-text-2)] max-w-2xl">
            Switch to a hospital admin account to operate clinical workflows, or create a new hospital from the
            <span className="font-semibold"> All Hospitals </span> page. Each hospital has its own MRN sequence,
            staff, custom fields and registers.
          </p>
        </div>
      )}
    </div>
  );
}

function QuickAction({ title, desc, icon: Icon, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="hms-card hms-card-hover p-5 text-left flex items-start gap-4"
    >
      <div className="h-11 w-11 grid place-items-center bg-[var(--hms-paper)] border border-[var(--hms-border-2)] rounded-sm">
        <Icon size={22} weight="bold" className="text-[var(--hms-ink)]" />
      </div>
      <div>
        <div className="font-heading font-bold">{title}</div>
        <div className="text-sm text-[var(--hms-text-2)]">{desc}</div>
      </div>
      <ArrowRight size={16} className="ml-auto text-[var(--hms-muted)]" weight="bold" />
    </button>
  );
}
